// Shared display formatters for billing amounts / rates / dates.

const CURRENCY_SYMBOL: Record<string, string> = {
  MNT: "₮",
  USD: "$",
  EUR: "€",
  CNY: "¥",
  JPY: "¥",
  KRW: "₩",
};

/** "5000000" → "5,000,000₮"  ·  null/empty/NaN → "—". Non-MNT uses the code. */
export function formatMoney(value: string | number | null | undefined, currency = "MNT"): string {
  if (value === null || value === undefined || value === "") return "—";
  const n = typeof value === "string" ? Number(value) : value;
  if (Number.isNaN(n)) return "—";
  const formatted = n.toLocaleString("en-US", { maximumFractionDigits: 2 });
  const sym = CURRENCY_SYMBOL[currency];
  return sym ? `${formatted}${sym}` : `${formatted} ${currency}`;
}

/** Compact MNT money for tight spaces (e.g. tier ranges on a card):
 *  1,500,000 → "1.5сая₮", 5,000,000 → "5сая₮", 250,000 → "250мянга₮". */
export function formatMoneyCompact(value: string | number | null | undefined, currency = "MNT"): string {
  if (value === null || value === undefined || value === "") return "—";
  const n = typeof value === "string" ? Number(value) : value;
  if (Number.isNaN(n)) return "—";
  const sym = currencySymbol(currency);
  const abbr = (x: number, suffix: string) => `${x % 1 === 0 ? String(x) : x.toFixed(1)}${suffix}${sym}`;
  const abs = Math.abs(n);
  if (abs >= 1e9) return abbr(n / 1e9, "тэрбум");
  if (abs >= 1e6) return abbr(n / 1e6, "сая");
  if (abs >= 1e3) return abbr(n / 1e3, "мянга");
  return `${n.toLocaleString("en-US")}${sym}`;
}

/** Decimal-or-percent string → "2.50%". null/empty/NaN → "—". */
export function formatPercent(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "—";
  const n = typeof value === "string" ? Number(value) : value;
  if (Number.isNaN(n)) return "—";
  return `${n.toFixed(2)}%`;
}

/** ISO string → "2026-05-27" (mn locale). null → "—". */
export function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("mn-MN");
}

/** Currency symbol for a code ("MNT" → "₮"), falling back to the code itself. */
export function currencySymbol(currency = "MNT"): string {
  return CURRENCY_SYMBOL[currency] ?? currency;
}

/** Number-only formatting (no currency): "5000000" → "5,000,000". null/NaN → "—". */
export function formatAmount(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "—";
  const n = typeof value === "string" ? Number(value) : value;
  if (Number.isNaN(n)) return "—";
  return n.toLocaleString("en-US", { maximumFractionDigits: 2 });
}

/** ISO string → Mongolian relative time ("3 өдрийн өмнө"). null → "—". */
export function formatRelativeTime(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 0) return formatDate(value);
  if (sec < 60) return "Дөнгөж сая";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} минутын өмнө`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} цагийн өмнө`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day} өдрийн өмнө`;
  const mon = Math.floor(day / 30);
  if (mon < 12) return `${mon} сарын өмнө`;
  return `${Math.floor(mon / 12)} жилийн өмнө`;
}
